// apps/web/src/pages/super/SuperSchedulerRunDetailPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import dayjs from "dayjs";
import { apiGet } from "../../utils/apiClient";

type RunSummary = {
    runId: string;
    job: string;
    status: string;
    totalShops: number;
    processed: number;
    success: number;
    error: number;
    insertedTotal?: number;
    startedAt?: string;
    finishedAt?: string;
};

type ShopRun = {
    id: string;
    shopUrl: string;
    status: "success" | "error";
    result?: any;
    error?: string;
    startedAt?: string;
    finishedAt?: string;
};

function badgeClass(status: string) {
    if (status === "success") return "bg-green-50 text-green-800 border-green-200";
    if (status === "partial_error") return "bg-yellow-50 text-yellow-800 border-yellow-200";
    if (status === "error") return "bg-red-50 text-red-800 border-red-200";
    if (status === "running") return "bg-blue-50 text-blue-800 border-blue-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
}

function fmt(v?: string) {
    if (!v) return "-";
    const d = dayjs(v);
    return d.isValid() ? d.format("YYYY/MM/DD HH:mm") : "-";
}

function safeStr(v: any) {
    try {
        if (v == null) return "";
        if (typeof v === "string") return v;
        return JSON.stringify(v);
    } catch {
        return String(v ?? "");
    }
}

export default function SuperSchedulerRunDetailPage() {
    const { id } = useParams();
    const [summary, setSummary] = useState<RunSummary | null>(null);
    const [shops, setShops] = useState<ShopRun[]>([]);
    const [loading, setLoading] = useState(true);

    // compact UI controls
    const [q, setQ] = useState("");
    const [onlyError, setOnlyError] = useState(false);
    const [expandOkJson, setExpandOkJson] = useState(false);

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const res = await apiGet(`/api/super/scheduler-runs/${encodeURIComponent(String(id ?? ""))}`);
                const json = await res.json();
                setSummary(json?.summary ?? null);
                setShops(Array.isArray(json?.shops) ? json.shops : []);
            } finally {
                setLoading(false);
            }
        }
        if (id) load();
    }, [id]);

    const errorShops = useMemo(() => shops.filter((s) => s.status === "error"), [shops]);

    const filteredShops = useMemo(() => {
        const k = q.trim().toLowerCase();
        return shops.filter((s) => {
            if (onlyError && s.status !== "error") return false;
            if (!k) return true;
            const hay =
                `${s.shopUrl} ${s.status} ${safeStr(s.error)} ${safeStr(s.result)}`.toLowerCase();
            return hay.includes(k);
        });
    }, [shops, q, onlyError]);

    // compact classes
    const page = "h-[calc(100vh-64px)] overflow-hidden p-2";
    const th = "border px-2 py-1 text-[11px] font-semibold bg-gray-100 whitespace-nowrap sticky top-0 z-10";
    const td = "border px-2 py-1 text-xs align-top";
    const mono = "font-mono";

    if (loading) return <div className="p-2 text-xs text-gray-500">Loading...</div>;
    if (!summary) return <div className="p-2 text-xs">データが見つかりません</div>;

    return (
        <div className={page}>
            <div className="h-full flex flex-col gap-2 overflow-hidden">
                {/* top bar */}
                <div className="shrink-0 flex items-center justify-between gap-2">
                    <Link to="/super/scheduler-runs" className="text-xs text-blue-600 hover:underline">
                        ← 実行履歴一覧へ戻る
                    </Link>

                    <div className="flex items-center gap-2">
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="shopUrl / error / JSON を検索"
                            className="border rounded px-2 py-1 text-xs w-64"
                        />

                        <label className="text-xs flex items-center gap-1 select-none">
                            <input
                                type="checkbox"
                                checked={onlyError}
                                onChange={(e) => setOnlyError(e.target.checked)}
                            />
                            エラーのみ
                        </label>

                        <label className="text-xs flex items-center gap-1 select-none">
                            <input
                                type="checkbox"
                                checked={expandOkJson}
                                onChange={(e) => setExpandOkJson(e.target.checked)}
                            />
                            OKのJSON展開
                        </label>
                    </div>
                </div>

                {/* summary card */}
                <div className="shrink-0 border rounded bg-white px-2 py-2">
                    <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{summary.job}</div>
                            <div className="text-[11px] text-gray-500">
                                runId: <span className={`${mono} break-all`}>{summary.runId}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            <span className={`px-2 py-0.5 rounded border text-xs ${badgeClass(summary.status)}`}>
                                {summary.status}
                            </span>
                            <div className="text-[11px] text-gray-600 whitespace-nowrap">
                                {summary.processed}/{summary.totalShops}
                            </div>
                        </div>
                    </div>

                    <div className="mt-2 grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
                        <div>
                            <div className="text-gray-500">開始</div>
                            <div className={mono}>{fmt(summary.startedAt)}</div>
                        </div>
                        <div>
                            <div className="text-gray-500">終了</div>
                            <div className={mono}>{fmt(summary.finishedAt)}</div>
                        </div>
                        <div>
                            <div className="text-gray-500">成功</div>
                            <div className="text-green-700 font-semibold">{summary.success}</div>
                        </div>
                        <div>
                            <div className="text-gray-500">失敗</div>
                            <div className="text-red-700 font-semibold">{summary.error}</div>
                        </div>
                        <div>
                            <div className="text-gray-500">inserted</div>
                            <div className={mono}>{typeof summary.insertedTotal === "number" ? summary.insertedTotal : "-"}</div>
                        </div>
                        <div>
                            <div className="text-gray-500">エラー店</div>
                            <div className={mono}>{errorShops.length}</div>
                        </div>
                    </div>
                </div>

                {/* error list (compact) */}
                {errorShops.length > 0 && (
                    <div className="shrink-0 border rounded bg-red-50 px-2 py-2">
                        <div className="text-xs font-semibold text-red-700">
                            エラーが発生したショップ（{errorShops.length}）
                        </div>
                        <div className="mt-1 max-h-28 overflow-auto space-y-1">
                            {errorShops.slice(0, 50).map((s) => (
                                <div key={s.id} className="text-[11px]">
                                    <div className={`${mono} truncate`} title={s.shopUrl}>
                                        {s.shopUrl}
                                    </div>
                                    {s.error && (
                                        <div className="text-[11px] text-red-700 truncate" title={s.error}>
                                            {s.error}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {errorShops.length > 50 && (
                                <div className="text-[11px] text-gray-600">※ 先頭 50 件のみ表示</div>
                            )}
                        </div>
                    </div>
                )}

                {/* table */}
                <div className="flex-1 min-h-0 overflow-auto border rounded bg-white">
                    <table className="min-w-[1000px] w-full border-collapse">
                        <colgroup>
                            <col style={{ width: "26rem" }} />
                            <col style={{ width: "6rem" }} />
                            <col style={{ width: "10rem" }} />
                            <col />
                        </colgroup>

                        <thead>
                            <tr>
                                <th className={th}>shopUrl</th>
                                <th className={th}>status</th>
                                <th className={th}>time</th>
                                <th className={th}>結果 / エラー</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredShops.length === 0 ? (
                                <tr>
                                    <td className={`${td} text-gray-500`} colSpan={4}>
                                        該当するデータがありません
                                    </td>
                                </tr>
                            ) : (
                                filteredShops.map((s) => {
                                    const timeText =
                                        s.startedAt || s.finishedAt ? `${fmt(s.startedAt)} → ${fmt(s.finishedAt)}` : "-";

                                    return (
                                        <tr key={s.id} className="hover:bg-gray-50">
                                            <td className={`${td} ${mono}`}>
                                                <div className="truncate" title={s.shopUrl}>
                                                    {s.shopUrl}
                                                </div>
                                            </td>

                                            <td className={`${td} text-center`}>
                                                <span className={`px-2 py-0.5 rounded border text-[11px] ${badgeClass(s.status)}`}>
                                                    {s.status}
                                                </span>
                                            </td>

                                            <td className={`${td} ${mono} text-[11px] whitespace-nowrap`}>{timeText}</td>

                                            <td className={td}>
                                                {s.status === "error" ? (
                                                    <pre className="text-[11px] whitespace-pre-wrap text-red-700">
                                                        {s.error || "-"}
                                                    </pre>
                                                ) : (
                                                    <details className="text-[11px]" open={expandOkJson}>
                                                        <summary className="cursor-pointer text-gray-600 select-none">
                                                            OK（クリックでJSON）
                                                        </summary>
                                                        <pre className="mt-1 whitespace-pre-wrap text-gray-600">
                                                            {JSON.stringify(s.result, null, 2)}
                                                        </pre>
                                                    </details>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="shrink-0 text-[11px] text-gray-500">
                    ※ 実行ログは TTL により自動削除されます
                </div>
            </div>
        </div>
    );
}
